'use client'

import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useSnapshot } from 'valtio';

import store from '@/store';
import { User } from '@/types/user';
import getUsers from '@/services/UserService/getUsers';
import { getCompanyId } from '@/utils/getCompanyId';
import Pagination, { PaginationRef } from '@/components/UiElements/Pagination';
import Splash from '@/components/splash/Splash';
import { ListInfoItem, ListRow } from '@/components/UiElements';
import getUserCompanyIds from '@/utils/getUserCompanyIds';

const perPage = 40;

type CompanyName = {
    id?: string | null;
    name?: string;
    store_name?: string;
}

const getUserCompanyNames = (user: User, companyNames: CompanyName[]) => {
    const userCompanyIds = getUserCompanyIds(user);

    const explicitNames = [
        (user as any).company_name,
        (user as any).companyName,
        ...(((user as any).companies || []) as Array<string | { name?: string; store_name?: string }>).map((company) => (
            typeof company === 'string' ? null : company?.name || company?.store_name
        )),
    ].filter(Boolean);

    const names = userCompanyIds.map((companyId) => {
        const company = companyNames.find((item) => item.id === companyId || item.store_name === companyId);
        return company?.name || company?.store_name;
    }).filter(Boolean);

    return [...new Set([...explicitNames, ...names].filter(Boolean))].join(', ');
};

const getCompanyOptions = (currentUser: any): CompanyName[] => {
    const companyNames = currentUser?.companyNames;
    if (Array.isArray(companyNames) && companyNames.length) {
        return companyNames;
    }

    return Array.isArray(currentUser?.companies) ? currentUser.companies : [];
};

const formatUserTitle = (user: User) => {
    if (!user.full_name) {
        return user.email || 'Unnamed User';
    }

    return user.full_name.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
};

type UserScrollProps = {
    filter: string
    onSelectedUserChange: (user: User) => void;
}

const UserScroll = ({
    filter,
    onSelectedUserChange,
}: UserScrollProps) => {
    const [data, setData] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const snap = useSnapshot(store)
    const companyNames = getCompanyOptions(snap.currentUser);
    const paginationRef = useRef<PaginationRef>(null);
    const isInitialMount = useRef(true);

    // const companyId = sessionStorage.getItem('companyId')
    const companyId = getCompanyId()

    const loadUsers = useCallback(async () => {
        if (!companyId) {
            setData([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response: any = await getUsers();
            const users = Array.isArray(response) ? response : response?.users;
            setData(users || []);
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers, snap?.selectedCompanyId]);

    const filteredData = useMemo(() => {
        return filter ? data.filter(user => {
          return Object.values(user).some(value => 
            String(value).toLowerCase().includes(filter.toLowerCase())
          );
        }) : [...data];
    }, [data, filter]);

    const total = useMemo(() => Math.ceil(filteredData.length / perPage), [filteredData]);

    const handleGetPageData = useCallback((pageNumber: number) => {
        const start = (pageNumber - 1) * perPage;
        const end = start + perPage;
        return filteredData.slice(start, end);
    }, [filteredData]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        paginationRef.current?.reset();
    }, [filteredData]);
    
    return (
        <div className='relative min-h-[100%]'>
            {loading && <Splash />}
            {(filteredData.length > 0 && total > 0) ? (
                <Pagination
                    ref={paginationRef}
                    theme={(user: User) => (
                        <ListRow
                            key={user.auth0_user_id || user.email}
                            title={formatUserTitle(user)}
                            onClick={() => onSelectedUserChange(user)}
                        >
                            <ListInfoItem label="Email" value={user.email} className="min-w-[260px] max-w-[320px]" />
                            <ListInfoItem label="First Name" value={user.first_name} className="min-w-[140px]" />
                            <ListInfoItem label="Last Name" value={user.last_name} className="min-w-[140px]" />
                            <ListInfoItem label="Companies" value={getUserCompanyNames(user, companyNames)} className="min-w-[260px] max-w-[420px]" />
                        </ListRow>
                    )}
                    total={total}
                    offset={150}
                    getPageData={handleGetPageData}
                />
            ) : (
                !loading && (
                    <div className={'flex flex-row justify-center items-center px-mx py-xl text-gray-300 bg-gray-50 rounded-md mt-xxl gap-lg h-[150px]'}>
                        No users match your current filters.
                    </div>
                )
            )}
        </div>
    );
}

export default UserScroll;
